// src/pages/UserManagement/UserList.tsx
// User list table component with actions

import React, { useState } from 'react';
import { Badge, Popover, PopoverBody, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { User } from './types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('user_management');

// Tập action-code tĩnh của cột "Thao tác" trong bảng danh sách người dùng — dùng để ẩn hẳn cột
// này khi role hiện tại không có quyền với bất kỳ action nào trong cột (button-upload-avatar
// không thuộc cột này nên không đưa vào đây, được gate riêng bằng canDisplay).
const USER_ACTION_COLUMN_CODES = [
  'button-edit',
  'button-permissions',
  'button-toggle-status',
  'button-delete',
];

// Mục 16b (Update), README_UPDATE.md — tên dài hơn ngưỡng này sẽ hiển thị rút gọn kèm dấu "...",
// bấm vào tên sẽ hiển thị popover với tên đầy đủ.
const NAME_TRUNCATE_LENGTH = 25;

// Đếm/cắt theo code point (Array.from tách chuỗi theo Unicode code point) thay vì .length/.slice
// thô (đếm theo UTF-16 code unit) — tên có emoji (VD "Nguyễn Văn Tèo 😎") sẽ bị cắt vỡ đôi
// surrogate pair nếu ngưỡng cắt rơi đúng giữa 1 emoji, ra ký tự lỗi (�) trên bảng danh sách.
const getNameCharLength = (name: string): number => Array.from(name).length;
const truncateNamePreservingUnicode = (name: string, length: number): string =>
  Array.from(name).slice(0, length).join('');

interface UserListProps {
  users: User[];
  loading: boolean;
  tenants: any[];
  roles: any[];
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onToggleStatus: (user: User) => void;
  onUploadAvatar: (user: User) => void;
  onUpdatePermissions: (user: User) => void;
  page: number;
  pageSize: number;
}

const UserList: React.FC<UserListProps> = ({
  users,
  loading,
  tenants,
  roles,
  onEdit,
  onDelete,
  onToggleStatus,
  onUploadAvatar,
  onUpdatePermissions,
  page,
  pageSize,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    USER_ACTION_COLUMN_CODES,
  );

  // Mục 16b — id user đang mở popover hiển thị tên đầy đủ (chỉ 1 popover mở tại 1 thời điểm)
  const [openNamePopoverId, setOpenNamePopoverId] = useState<number | string | null>(null);

  // Helper function to get department name by ID (department = Tenant hierarchy node ở BE)
  const getDepartmentName = (departmentId?: number) => {
    if (!departmentId) return '-';
    const department = tenants.find(t => t.id === departmentId);
    return department?.name || st('chua_phan_phong_text');
  };
  
  // Helper function to get role name by ID
  const getRoleName = (roleId?: number) => {
    if (!roleId) return '-';
    const role = roles.find(r => r.id === roleId);
    return role?.name || st('quan_ly_text');
  };
  
  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return st('chua_dang_nhap_text');
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // Loading skeleton
  if (loading) {
    return (
      <DataTableShell
        headerRow={
          <tr>
            <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
            <th>{st('name_label')}</th>
            <th>{st('ten_dang_nhap_column')}</th>
            <th>{st('email_label')}</th>
            <th>{st('phong_ban_label')}</th>
            <th>{st('vai_tro_label_2')}</th>
            <th>{st('trang_thai_label')}</th>
            <th>{st('dang_nhap_cuoi_column')}</th>
            {canShowActionColumn && <th>{st('thao_tac_column')}</th>}
          </tr>
        }
      >
        {[...Array(5)].map((_, idx) => (
          <tr key={idx}>
            <td><div className="placeholder-glow"><span className="placeholder col-4"></span></div></td>
            <td><div className="placeholder-glow"><span className="placeholder col-10"></span></div></td>
            <td><div className="placeholder-glow"><span className="placeholder col-7"></span></div></td>
            <td><div className="placeholder-glow"><span className="placeholder col-8"></span></div></td>
            <td><div className="placeholder-glow"><span className="placeholder col-6"></span></div></td>
            <td><div className="placeholder-glow"><span className="placeholder col-5"></span></div></td>
            <td><div className="placeholder-glow"><span className="placeholder col-6"></span></div></td>
            <td><div className="placeholder-glow"><span className="placeholder col-8"></span></div></td>
            {canShowActionColumn && (
              <td><div className="placeholder-glow"><span className="placeholder col-6"></span></div></td>
            )}
          </tr>
        ))}
      </DataTableShell>
    );
  }
  
  // Empty state
  if (users.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <i className="ri-user-line display-3 text-muted"></i>
        </div>
        <h5 className="mt-3">{st('khong_tim_thay_nguoi_dung_title')}</h5>
        <p className="text-muted mb-4">{st('thu_thay_doi_dieu_kien_loc_text')}</p>
      </div>
    );
  }
  
  return (
    <>
      <DataTableShell
        headerRow={
          <tr>
            <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
            <th>{st('name_label')}</th>
            <th>{st('ten_dang_nhap_column')}</th>
            <th>{st('email_label')}</th>
            <th>{st('phong_ban_label')}</th>
            <th>{st('vai_tro_label_2')}</th>
            <th style={{ width: '100px' }}>{st('trang_thai_label')}</th>
            <th>{st('dang_nhap_cuoi_column')}</th>
            {canShowActionColumn && (
              <th style={{ width: '180px' }} className="text-center">{st('thao_tac_column')}</th>
            )}
          </tr>
        }
      >
          {users.map((user, rowIndex) => (
            <tr key={user.id}>
              <td className="text-center">{getSttNumber(page, pageSize, rowIndex)}</td>
              {/* Avatar + Name */}
              <td>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0 me-3 position-relative avatar-group">
                    <img
                      src={user.avatar || '/icon.jpg'}
                      alt={user.name}
                      className="avatar-sm rounded-circle"
                    />
                    {PageActionPermission.canDisplay(location.pathname, "button-upload-avatar") && (
                      <>
                        <button
                          className="btn btn-sm btn-soft-primary avatar-sm rounded-circle position-absolute top-0 start-0 avatar-hover-overlay"
                          data-action-code="button-upload-avatar"
                          data-action-name="Đổi ảnh đại diện"
                          onClick={() => onUploadAvatar(user)}
                          id={`avatar-btn-${user.id}`}
                        >
                          <i className="ri-camera-line"></i>
                        </button>
                        <UncontrolledTooltip placement="top" target={`avatar-btn-${user.id}`}>
                          {st('doi_anh_dai_dien_text')}
                        </UncontrolledTooltip>
                      </>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <h5
                      className="fs-14 mb-1"
                      id={`user-name-${user.id}`}
                      style={
                        getNameCharLength(user.name) > NAME_TRUNCATE_LENGTH
                          ? { cursor: 'pointer' }
                          : undefined
                      }
                      onClick={() =>
                        getNameCharLength(user.name) > NAME_TRUNCATE_LENGTH &&
                        setOpenNamePopoverId(user.id)
                      }
                    >
                      {getNameCharLength(user.name) > NAME_TRUNCATE_LENGTH
                        ? `${truncateNamePreservingUnicode(user.name, NAME_TRUNCATE_LENGTH)}...`
                        : user.name}
                    </h5>
                    {getNameCharLength(user.name) > NAME_TRUNCATE_LENGTH && (
                      <Popover
                        placement="top"
                        isOpen={openNamePopoverId === user.id}
                        target={`user-name-${user.id}`}
                        toggle={() => setOpenNamePopoverId(null)}
                        trigger="legacy"
                      >
                        <PopoverBody>{user.name}</PopoverBody>
                      </Popover>
                    )}
                    {user.phoneNumber && (
                      <p className="text-muted mb-0">
                        <i className="ri-phone-line me-1"></i>
                        {user.phoneNumber}
                      </p>
                    )}
                  </div>
                </div>
              </td>

              {/* Username để nhân viên tra cứu và cấp thông tin đăng nhập cho tài khoản vừa tạo */}
              <td className="text-break">{user.username || '-'}</td>
              
              {/* Email */}
              <td>
                <span className="text-muted">{user.email}</span>
              </td>
              
              {/* Tenant/Department */}
              <td>
                <span className="text-muted">{user.departmentName || getDepartmentName(user.departmentId)}</span>
                {/* <Badge color="light" className="text-muted">
                  {user.departmentName || getDepartmentName(user.departmentId)}
                </Badge> */}
              </td>
              
              {/* Role */}
              <td>
                <Badge color="primary">
                  {user.roleName || getRoleName(user.roleId)}
                </Badge>
              </td>
              
              {/* Status */}
              <td>
                <Badge
                  color={user.status === 'Active' ? 'success' : 'danger'}
                  className="d-inline-flex align-items-center"
                >
                  <i className={`ri-${user.status === 'Active' ? 'checkbox' : 'close'}-circle-line me-1`}></i>
                  {user.status === 'Active' ? st('hoat_dong_option') : user.status === 'Locked' ? st('da_khoa_option') : user.status}
                </Badge>
              </td>
              
              {/* Last Login */}
              <td>
                <small className="text-muted">
                  {formatDate(user.lastLoginDate)}
                </small>
              </td>
              
              {/* Actions */}
              {canShowActionColumn && (
                <td>
                  <div className="d-flex gap-1 justify-content-center">
                    {/* Edit */}
                    {PageActionPermission.canDisplay(location.pathname, "button-edit") && (
                      <>
                        <button
                          className="btn btn-sm btn-soft-info"
                          data-action-code="button-edit"
                          data-action-name="Sửa người dùng"
                          onClick={() => onEdit(user)}
                          id={`edit-btn-${user.id}`}
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <UncontrolledTooltip placement="top" target={`edit-btn-${user.id}`}>
                          {st('edit_user_text')}
                        </UncontrolledTooltip>
                      </>
                    )}

                    {/* Permissions */}
                    {PageActionPermission.canDisplay(location.pathname, "button-permissions") && (
                      <>
                        <button
                          className="btn btn-sm btn-soft-secondary"
                          data-action-code="button-permissions"
                          data-action-name="Cập nhật quyền người dùng"
                          onClick={() => onUpdatePermissions(user)}
                          id={`perm-btn-${user.id}`}
                        >
                          <i className="ri-shield-user-line"></i>
                        </button>
                        <UncontrolledTooltip placement="top" target={`perm-btn-${user.id}`}>
                          {st('permissions_text')}
                        </UncontrolledTooltip>
                      </>
                    )}

                    {/* Lock/Unlock */}
                    {PageActionPermission.canDisplay(location.pathname, "button-toggle-status") && (
                      <>
                        <button
                          className={`btn btn-sm btn-soft-${user.status === 'Active' ? 'warning' : 'success'}`}
                          data-action-code="button-toggle-status"
                          data-action-name="Khóa/Mở khóa người dùng"
                          onClick={() => onToggleStatus(user)}
                          id={`status-btn-${user.id}`}
                        >
                          <i className={`ri-${user.status === 'Active' ? 'lock' : 'lock-unlock'}-line`}></i>
                        </button>
                        <UncontrolledTooltip placement="top" target={`status-btn-${user.id}`}>
                          {user.status === 'Active' ? st('khoa_tai_khoan_text') : st('mo_khoa_tai_khoan_text')}
                        </UncontrolledTooltip>
                      </>
                    )}

                    {/* Delete */}
                    {PageActionPermission.canDisplay(location.pathname, "button-delete") && (
                      <>
                        <button
                          className="btn btn-sm btn-soft-danger"
                          data-action-code="button-delete"
                          data-action-name="Xóa người dùng"
                          onClick={() => onDelete(user)}
                          id={`delete-btn-${user.id}`}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                        <UncontrolledTooltip placement="top" target={`delete-btn-${user.id}`}>
                          {st('delete_user_text')}
                        </UncontrolledTooltip>
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
      </DataTableShell>

      {/* CSS for hover effect on avatar */}
      <style>{`
        .avatar-group {
          position: relative;
        }
        .avatar-hover-overlay {
          opacity: 0;
          transition: opacity 0.2s;
          background: rgba(0, 0, 0, 0.6) !important;
        }
        .avatar-group:hover .avatar-hover-overlay {
          opacity: 1 !important;
        }
        .avatar-hover-overlay i {
          color: white !important;
        }
      `}</style>
    </>
  );
};

export default UserList;
